import React, { useEffect } from 'react'
import {
  View,
  StyleSheet,
  useWindowDimensions,
  TouchableOpacity,
} from 'react-native'
import { Text } from 'react-native-paper'
import { useTranslation } from 'react-i18next'
import {
  BtCheckBox,
  BtInput,
  BtText,
  PhoneInputWithParentheses,
} from '@linqpal/components/src/ui'
import { Formik, useFormikContext } from 'formik'
import {
  validateEmailField,
  validatePhoneNumberField,
  validateRequiredField,
} from '../../helpers/validationHelpers'
import ErrorTextComponent from '@linqpal/components/src/ui/ErrorTextComponent'
import { IFormData, InputFieldNames } from './types'
import { formatPhoneNumberToInternational } from '../../helpers/formatPhoneNumber'

interface ContactInformationProps {
  onValidationChange: (
    isValid: boolean,
    validPhone: string,
    values: IFormData,
  ) => void
  hideHeaderTitle?: boolean
  initialValues: IFormData
}

const ContactInformationForm: React.FC<ContactInformationProps> = ({
  onValidationChange,
  hideHeaderTitle,
  initialValues,
}) => {
  const { t } = useTranslation('global')

  const { width } = useWindowDimensions()
  const isSmallScreen = width < 1320

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    validateField,
  } = useFormikContext<IFormData>()

  useEffect(() => {
    const hasErrors = Object.values(errors).some((error) => error)
    const hasPhoneErrors = !!errors[InputFieldNames.PHONE]

    onValidationChange(
      !hasErrors,
      hasPhoneErrors
        ? ''
        : formatPhoneNumberToInternational(values[InputFieldNames.PHONE]),
      values,
    )
  }, [onValidationChange, errors, values])

  useEffect(() => {
    if (initialValues) {
      Object.entries(initialValues).forEach(([field, value]) => {
        validateField(field)
        if (value) {
          setFieldTouched(field, true)
        }
      })
    }
  }, [initialValues, setFieldValue, setFieldTouched, validateField])

  return (
    <View style={styles.container}>
      {!hideHeaderTitle && (
        <>
          <Text style={styles.sectionTitle}>
            {t('integrations.pay-as-guest.contact-section.contact-information')}
          </Text>
          <Text style={styles.sectionDescription}>
            {t(
              'integrations.pay-as-guest.contact-section.contact-information-details',
            )}
          </Text>
        </>
      )}

      <View
        style={
          (styles.inputRow,
          isSmallScreen ? styles.inputColumn : styles.inputRow)
        }
      >
        <BtInput
          size="medium"
          value={values[InputFieldNames.FIRST_NAME]}
          status={
            errors[InputFieldNames.FIRST_NAME] &&
            touched[InputFieldNames.FIRST_NAME]
              ? 'danger'
              : ''
          }
          onChangeText={handleChange(InputFieldNames.FIRST_NAME)}
          onBlur={() => {
            setFieldTouched(InputFieldNames.FIRST_NAME, true)
            handleBlur(InputFieldNames.FIRST_NAME)
          }}
          //validateInput={(value: string) => validateRequiredField(value, t)}
          testID="guestFirstName"
          label={t('integrations.pay-as-guest.contact-section.first-name')}
          style={[
            styles.input,
            !!errors[InputFieldNames.FIRST_NAME] &&
              touched[InputFieldNames.FIRST_NAME] &&
              styles.inputError,
          ]}
          labelStyle={[
            styles.inputPlaceholder,
            !!errors[InputFieldNames.FIRST_NAME] &&
              touched[InputFieldNames.FIRST_NAME] &&
              styles.inputPlaceholderError,
          ]}
          textStyle={styles.inputText}
          errorTextComponent={
            <ErrorTextComponent
              text={
                errors[InputFieldNames.FIRST_NAME] &&
                touched[InputFieldNames.FIRST_NAME]
                  ? errors[InputFieldNames.FIRST_NAME]
                  : ''
              }
            />
          }
          mandatory
        />
        <BtInput
          size="medium"
          value={values[InputFieldNames.LAST_NAME]}
          status={
            errors[InputFieldNames.LAST_NAME] &&
            touched[InputFieldNames.LAST_NAME]
              ? 'danger'
              : ''
          }
          onChangeText={handleChange(InputFieldNames.LAST_NAME)}
          onBlur={() => {
            setFieldTouched(InputFieldNames.LAST_NAME, true)
            handleBlur(InputFieldNames.LAST_NAME)
          }}
          //validateInput={(value: string) => validateRequiredField(value, t)}
          testID="guestLastName"
          label={t('integrations.pay-as-guest.contact-section.last-name')}
          style={[
            styles.input,
            !!errors[InputFieldNames.LAST_NAME] &&
              touched[InputFieldNames.LAST_NAME] &&
              styles.inputError,
          ]}
          errorTextComponent={
            <ErrorTextComponent
              text={
                errors[InputFieldNames.LAST_NAME] &&
                touched[InputFieldNames.LAST_NAME]
                  ? errors[InputFieldNames.LAST_NAME]
                  : ''
              }
            />
          }
          mandatory
          labelStyle={[
            styles.inputPlaceholder,
            !!errors[InputFieldNames.LAST_NAME] &&
              touched[InputFieldNames.LAST_NAME] &&
              styles.inputPlaceholderError,
          ]}
          textStyle={styles.inputText}
        />
      </View>

      <View
        style={
          (styles.inputRow,
          isSmallScreen ? styles.inputColumn : styles.inputRow)
        }
      >
        <BtInput
          size="medium"
          value={values[InputFieldNames.EMAIL]}
          status={
            errors[InputFieldNames.EMAIL] && touched[InputFieldNames.EMAIL]
              ? 'danger'
              : ''
          }
          onChangeText={handleChange(InputFieldNames.EMAIL)}
          onBlur={() => {
            setFieldTouched(InputFieldNames.EMAIL, true)
            handleBlur(InputFieldNames.EMAIL)
          }}
          //validateInput={(value: string) => validateEmailField(value, t)}
          testID="guestEmail"
          label={t('integrations.pay-as-guest.contact-section.email-address')}
          style={[
            styles.input,
            !!errors[InputFieldNames.EMAIL] &&
              touched[InputFieldNames.EMAIL] &&
              styles.inputError,
          ]}
          errorTextComponent={
            <ErrorTextComponent
              text={
                touched[InputFieldNames.EMAIL] && errors[InputFieldNames.EMAIL]
                  ? errors[InputFieldNames.EMAIL]
                  : ''
              }
            />
          }
          labelStyle={[
            styles.inputPlaceholder,
            !!errors[InputFieldNames.EMAIL] &&
              touched[InputFieldNames.EMAIL] &&
              styles.inputPlaceholderError,
          ]}
          textStyle={styles.inputText}
          mandatory
        />
        <PhoneInputWithParentheses
          value={values[InputFieldNames.PHONE]}
          status={
            errors[InputFieldNames.PHONE] && touched[InputFieldNames.PHONE]
              ? 'danger'
              : ''
          }
          size="medium"
          testID="guestPhone"
          label={t('integrations.pay-as-guest.contact-section.phone-number')}
          labelStyle={[
            styles.inputPlaceholder,
            !!errors[InputFieldNames.PHONE] &&
              touched[InputFieldNames.PHONE] &&
              styles.inputPlaceholderError,
          ]}
          validate={(value) => validatePhoneNumberField(value, t)}
          onChangeText={(val) =>
            setFieldValue(InputFieldNames.PHONE, val.parsed)
          }
          onBlur={() => {
            setFieldTouched(InputFieldNames.PHONE, true)
            handleBlur(InputFieldNames.PHONE)
          }}
          style={[
            styles.input,
            !!errors[InputFieldNames.PHONE] &&
              touched[InputFieldNames.PHONE] &&
              styles.inputError,
          ]}
          textStyle={styles.inputText}
          required
        />
      </View>
      <View
        style={
          (styles.singleInputRow,
          isSmallScreen ? styles.inputColumn : styles.singleInputRow)
        }
      >
        <BtInput
          size="medium"
          value={values[InputFieldNames.BUSINESS_NAME]}
          status={
            errors[InputFieldNames.BUSINESS_NAME] &&
            touched[InputFieldNames.BUSINESS_NAME]
              ? 'danger'
              : ''
          }
          onChangeText={handleChange(InputFieldNames.BUSINESS_NAME)}
          onBlur={() => {
            setFieldTouched(InputFieldNames.BUSINESS_NAME, true)
            handleBlur(InputFieldNames.BUSINESS_NAME)
          }}
          testID="guestBusinessName"
          label={t('integrations.pay-as-guest.contact-section.business-name')}
          style={[
            styles.input,
            !!errors[InputFieldNames.BUSINESS_NAME] &&
              touched[InputFieldNames.BUSINESS_NAME] &&
              styles.inputError,
          ]}
          errorTextComponent={
            <ErrorTextComponent
              text={
                touched[InputFieldNames.BUSINESS_NAME] &&
                errors[InputFieldNames.BUSINESS_NAME]
                  ? errors[InputFieldNames.BUSINESS_NAME]
                  : ''
              }
            />
          }
          labelStyle={[
            styles.inputPlaceholder,
            !!errors[InputFieldNames.BUSINESS_NAME] &&
              touched[InputFieldNames.BUSINESS_NAME] &&
              styles.inputPlaceholderError,
          ]}
          textStyle={styles.inputText}
          mandatory={values[InputFieldNames.IS_BUSINESS]}
          disabled={!values[InputFieldNames.IS_BUSINESS]}
        />
      </View>
      <TouchableOpacity
        style={styles.checkboxWrapper}
        onPress={() =>
          setFieldValue(
            InputFieldNames.IS_BUSINESS,
            !values[InputFieldNames.IS_BUSINESS],
          )
        }
        activeOpacity={0.7}
      >
        <BtCheckBox
          style={styles.checkbox}
          checked={!values[InputFieldNames.IS_BUSINESS]}
          onPress={() =>
            setFieldValue(
              InputFieldNames.IS_BUSINESS,
              !values[InputFieldNames.IS_BUSINESS],
            )
          }
        />
        <BtText style={styles.checkboxLabel}>
          {t('integrations.pay-as-guest.contact-section.not-a-business')}
        </BtText>
      </TouchableOpacity>
    </View>
  )
}

export default function ContactInformation({
  onValidationChange,
  hideHeaderTitle,
  initialValues,
}: ContactInformationProps) {
  const { t } = useTranslation('global')

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validate={(values: IFormData) => {
        const errors: { [key: string]: string } = {}
        errors[InputFieldNames.FIRST_NAME] =
          validateRequiredField(values[InputFieldNames.FIRST_NAME], t) || ''
        errors[InputFieldNames.LAST_NAME] =
          validateRequiredField(values[InputFieldNames.LAST_NAME], t) || ''
        errors[InputFieldNames.EMAIL] =
          validateEmailField(values[InputFieldNames.EMAIL], t) || ''
        errors[InputFieldNames.PHONE] =
          validatePhoneNumberField(values[InputFieldNames.PHONE] ?? '', t) || ''
        errors[InputFieldNames.BUSINESS_NAME] = values[
          InputFieldNames.IS_BUSINESS
        ]
          ? validateRequiredField(values[InputFieldNames.BUSINESS_NAME], t) ||
            ''
          : ''
        return errors
      }}
      onSubmit={(values: IFormData) => {
        console.log('Form submitted with:', values)
      }}
    >
      {() => (
        <ContactInformationForm
          onValidationChange={onValidationChange}
          hideHeaderTitle={hideHeaderTitle}
          initialValues={initialValues}
        />
      )}
    </Formik>
  )
}

const styles = StyleSheet.create({
  checkboxWrapper: { flexDirection: 'row', marginBottom: 30 },
  checkbox: { width: 20, height: 20, marginRight: 11 },
  checkboxLabel: {
    fontWeight: '500',
    fontSize: 14,
    lineHeight: 20,
    color: '#001929',
    fontFamily: 'Inter',
  },
  container: {
    marginBottom: 24,
    minHeight: 230,
    display: 'flex',
    flexShrink: 1,
  },
  sectionTitle: {
    fontSize: 20,
    lineHeight: 28,
    fontWeight: '700',
    fontFamily: 'Inter',
    color: '#001929',
    marginBottom: 4,
  },
  sectionDescription: {
    fontSize: 14,
    lineHeight: 22,
    fontWeight: '500',
    fontFamily: 'Inter',
    color: '#001929',
    marginBottom: 18,
  },
  inputRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 48,
    gap: 24,
  },
  singleInputRow: {
    flexDirection: 'row',
    marginBottom: 38,
  },
  inputColumn: {
    flexDirection: 'column',
    gap: 24,
    marginBottom: 24,
  },
  input: {
    height: 40,
    minWidth: 200,
    flex: 1,
    borderRadius: 4,
    borderColor: '#99ADBA',
  },
  inputText: {
    fontFamily: 'Inter',
    fontWeight: '600',
    fontSize: 14,
    lineHeight: 16,
    color: '#001929',
  },
  inputError: {
    color: '#EC002A',
    borderWidth: 2,
    backgroundColor: '#FFF2F4',
    borderColor: '#EC002A',
  },
  inputPlaceholder: {
    fontFamily: 'Inter',
    fontWeight: '400',
    fontSize: 12,
    lineHeight: 16,
    color: '#001929',
    marginBottom: 1,
  },
  inputPlaceholderError: {
    color: '#EC002A',
  },
  errorMessage: {
    fontFamily: 'Inter',
    fontWeight: '400',
    fontSize: 12,
    lineHeight: 14.4,
    color: '#EC002A',
  },
})
